import SuperFetch from 'library/helpers/superFetch';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { devicesActions } from './slice';

export function* getDevices() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const devices = yield call(() => SuperFetch.get('/devices', {}, { includeFields: true }));

		if (devices.statusCode === 200) {
			yield put(devicesActions.loadDevicesSuccess({ orgID, data: devices.data }));
		} else {
			throw Error(devices.error.message || 'Failed to load devices.');
		}
	} catch (error) {
		yield put(devicesActions.loadDevicesError(error));
	}
}

export function* getDevicesSuccess() {
	yield put(devicesActions.loadAllDevicesLocations());
}

export function* getAllDevicesLocations() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const devices = yield call(() => SuperFetch.get('/devices/getAllDevicesAllLocations'));
		if (devices.statusCode === 200) {
			yield put(devicesActions.loadAllDevicesLocationsSuccess({ orgID, data: devices.data }));
		} else {
			throw Error(devices.error.message || 'Failed to load devices locations.');
		}
	} catch (error) {
		yield put(devicesActions.loadAllDevicesLocationsError(error));
	}
}

export function* createDevice({ payload }) {
	try {
		const { fields, categoryID, name, floor: floorID, mapID, coordinates } = payload;

		const fieldsData = fields
			? fields
					.filter((field) => field.fieldID && (field.value || field.value === 0))
					.map((field) => ({
						fieldID: field.fieldID,
						value: field.value
					}))
			: [];

		const device = yield call(() => SuperFetch.post('/devices', { categoryID, name }));
		if (device.statusCode === 201 || device.statusCode === 200) {
			if (fieldsData.length > 0) {
				yield call(createDeviceField, { payload: { deviceID: device.data, data: { fields: fieldsData } } });
			}
			if (coordinates && coordinates.x != null && coordinates.y != null) {
				yield call(createDeviceLocation, {
					payload: {
						deviceID: device.data,
						data: { xAxis: coordinates.x, yAxis: coordinates.y, mapID, floorID }
					}
				});
			}
			Notification('success', 'Device Added Successfully.');

			yield put(devicesActions.createDeviceSuccess());
		} else {
			Notification('error', 'Failed to add device.');
			throw Error(device.error.message || 'Failed to create device.');
		}
	} catch (error) {
		yield put(devicesActions.createDeviceError(error));
	}
}

export function* createMultipleDevices({ payload }) {
	try {
		const { devices: deviceCount, name: baseName, ...restPayload } = payload;
		const numberOfDevices = deviceCount || 1;
		let successCount = 0;
		let failureCount = 0;

		for (let i = 1; i <= numberOfDevices; i++) {
			try {
				const deviceName = numberOfDevices > 1 ? `${baseName} ${i}` : baseName;
				const {
					fields,
					categoryID,
					floor: floorID,
					mapID,
					coordinates
				} = {
					...restPayload,
					name: deviceName
				};

				const fieldsData = fields
					? fields
							.filter((field) => field.fieldID && (field.value || field.value === 0))
							.map((field) => ({
								fieldID: field.fieldID,
								value: field.value
							}))
					: [];

				const device = yield call(() => SuperFetch.post('/devices', { categoryID, name: deviceName }));
				if (device.statusCode === 201 || device.statusCode === 200) {
					if (fieldsData.length > 0) {
						try {
							yield call(createDeviceField, {
								payload: { deviceID: device.data, data: { fields: fieldsData } }
							});
						} catch (fieldError) {
							console.error(`Failed to create fields for device ${i}:`, fieldError);
						}
					}
					if (coordinates && coordinates.x != null && coordinates.y != null) {
						try {
							yield call(createDeviceLocation, {
								payload: {
									deviceID: device.data,
									data: { xAxis: coordinates.x, yAxis: coordinates.y, mapID, floorID }
								}
							});
						} catch (locationError) {
							console.error(`Failed to create location for device ${i}:`, locationError);
						}
					}
					successCount++;
				} else {
					failureCount++;
					console.error(`Failed to create device ${i}: ${device.error?.message}`);
				}

				// Add a small delay between requests
				if (i < numberOfDevices) {
					yield call(() => new Promise((resolve) => setTimeout(resolve, 200)));
				}
			} catch (error) {
				failureCount++;
				console.error(`Failed to create device ${i}:`, error);
			}
		}

		if (successCount > 0) {
			const message =
				numberOfDevices > 1
					? `Successfully created ${successCount} of ${numberOfDevices} devices.${
							failureCount > 0 ? ` ${failureCount} failed.` : ''
						}`
					: 'Device created successfully.';
			Notification('success', message);
			yield put(devicesActions.createMultipleDevicesSuccess());
		} else {
			throw new Error('Failed to create any devices.');
		}
	} catch (error) {
		Notification('error', error.message || 'Failed to create devices.');
		yield put(devicesActions.createMultipleDevicesError(error));
	}
}

//🔥 Actions here!, After adding device
export function* createDeviceSuccess() {
	yield put(devicesActions.loadDevices());
}

//🔥 Actions here!, After adding multiple devices
export function* createMultipleDevicesSuccess() {
	yield put(devicesActions.loadDevices());
}

export function* updateDevice({ payload }) {
	try {
		const { deviceID, data } = payload;
		const device = yield call(() => SuperFetch.patch(`/devices/${deviceID}`, data));
		if (device.statusCode === 200) {
			Notification('success', 'Device name updated successfully.');
			yield put(devicesActions.updateDeviceSuccess({ deviceID }));
		} else {
			throw Error(device.error.message || 'Failed to update device name.');
		}
	} catch (error) {
		yield put(devicesActions.updateDeviceError(error));
	}
}

//🔥 Actions here!, After updating device
export function* updateDeviceSuccess() {
	yield put(devicesActions.loadDevices());
}

export function* deleteDevice({ payload }) {
	try {
		const { deviceID } = payload;
		const device = yield call(() => SuperFetch.delete(`/devices/${deviceID}`));
		if (device.statusCode === 200) {
			Notification('success', 'Device deleted.');
			yield put(devicesActions.deleteDeviceSuccess());
		} else {
			throw Error(device.error.message || 'Failed to delete device.');
		}
	} catch (error) {
		Notification('error', error.message || 'Delete Failed, Please try again.');
		yield put(devicesActions.deleteDeviceError(error));
	}
}

//🔥 Actions here!, After deleting device
export function* deleteDeviceSuccess() {
	yield put(devicesActions.loadDevices());
}

export function* getDeviceFields({ payload }) {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const { deviceID } = payload;
		const fields = yield call(() => SuperFetch.get(`/devices/${deviceID}/fields`));

		if (fields.statusCode === 200) {
			yield put(devicesActions.loadDeviceFieldsSuccess({ orgID, deviceID, fields: fields.data }));
		}
	} catch (error) {
		yield put(devicesActions.loadDeviceFieldsError(error));
	}
}

export function* createDeviceField({ payload }) {
	try {
		const { deviceID, data } = payload;
		const field = yield call(() => SuperFetch.post(`/devices/${deviceID}/fields`, data));
		if (field.statusCode === 201 || field.statusCode === 200) {
			yield put(devicesActions.createDeviceFieldSuccess({ deviceID }));
			Notification('success', 'Device field created successfully.');
		} else {
			throw Error(field.error?.message || field.error || 'Failed to create device field.');
		}
	} catch (error) {
		Notification('error', error.message || 'Failed to create device field.');
		yield put(devicesActions.createDeviceFieldError(error));
	}
}

//🔥 Actions here!, After adding device field
export function* createDeviceFieldSuccess({ payload }) {
	const { deviceID } = payload;
	yield put(devicesActions.loadDeviceFields({ deviceID }));
}

export function* updateDeviceField({ payload }) {
	try {
		const { deviceID, data } = payload;
		const field = yield call(() => SuperFetch.post(`/devices/${deviceID}/fields`, data));
		if (field.statusCode === 201 || field.statusCode === 200) {
			yield put(devicesActions.updateDeviceFieldSuccess({ deviceID }));
			Notification('success', 'Device field updated successfully.');
		} else {
			throw Error(field.error?.message || field.error || 'Failed to update device field.');
		}
	} catch (error) {
		Notification('error', error.message || 'Failed to update device field.');
		yield put(devicesActions.updateDeviceFieldError(error));
	}
}

//🔥 Actions here!, After adding device field
export function* updateDeviceFieldSuccess({ payload }) {
	const { deviceID } = payload;
	yield put(devicesActions.loadDeviceFields({ deviceID }));
}

export function* deleteDeviceField({ payload }) {
	try {
		const { deviceID, fieldID } = payload;
		const field = yield call(() => SuperFetch.delete(`/devices/${deviceID}/fields/${fieldID}`));
		if (field.statusCode === 200) {
			Notification('success', 'Device field deleted successfully.');
			yield put(devicesActions.deleteDeviceFieldSuccess({ deviceID }));
		} else {
			throw Error(field.error?.message || field.error || 'Failed to delete device field.');
		}
	} catch (error) {
		Notification('error', error.message || 'Failed to delete device field.');
		yield put(devicesActions.deleteDeviceFieldError(error));
	}
}
//🔥 Actions here!, After deleting device field
export function* deleteDeviceFieldSuccess({ payload }) {
	const { deviceID } = payload;
	yield put(devicesActions.loadDeviceFields({ deviceID }));
}

export function* getDeviceLocation({ payload }) {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const { deviceID } = payload;
		const location = yield call(() => SuperFetch.get(`/devices/${deviceID}/location`));
		if (location.statusCode === 200) {
			yield put(devicesActions.loadDeviceLocationSuccess({ orgID, deviceID, location: location.data }));
		} else {
			throw Error(location.error.message || 'Failed to load device location.');
		}
	} catch (error) {
		yield put(devicesActions.loadDeviceLocationError(error));
	}
}

export function* createDeviceLocation({ payload }) {
	try {
		const { deviceID, data } = payload;
		const location = yield call(() => SuperFetch.post(`/devices/${deviceID}/location`, data));
		if (location.statusCode === 201 || location.statusCode === 200) {
			yield put(devicesActions.createDeviceLocationSuccess({ deviceID }));
			Notification('success', 'Device location created successfully.');
		} else {
			throw Error(location.error.message || 'Failed to create device location.');
		}
	} catch (error) {
		Notification('error', error.message || 'Failed to create device location.');

		yield put(devicesActions.createDeviceLocationError(error));
	}
}

//🔥 Actions here!, After adding device location
export function* createDeviceLocationSuccess({ payload }) {
	const { deviceID } = payload;
	yield put(devicesActions.loadDeviceLocation({ deviceID }));
}

export function* updateDeviceLocation({ payload }) {
	try {
		const { deviceID, locationID, data } = payload;
		const location = yield call(() => SuperFetch.patch(`/devices/${deviceID}/location/${locationID}`, data));
		if (location.statusCode === 200) {
			Notification('success', 'Device location updated successfully.');
			yield put(devicesActions.updateDeviceLocationSuccess({ deviceID }));
		} else {
			throw Error(location.error.message || 'Failed to update device location.');
		}
	} catch (error) {
		Notification('error', error.message || 'Failed to update device location.');
		yield put(devicesActions.updateDeviceLocationError(error));
	}
}

//🔥 Actions here!, After updating device location
export function* updateDeviceLocationSuccess({ payload }) {
	const { deviceID } = payload;
	yield put(devicesActions.loadDeviceLocation({ deviceID }));
}

export function* deleteDeviceLocation({ payload }) {
	try {
		const { deviceID, locationID } = payload;
		const location = yield call(() => SuperFetch.delete(`/devices/${deviceID}/location/${locationID}`));
		if (location.statusCode === 200) {
			yield put(devicesActions.deleteDeviceLocationSuccess({ deviceID }));
			Notification('success', 'Device removed successfully.');
		} else {
			Notification('error', 'Failed to delete device location.');
			throw Error(location.error.message || 'Failed to delete device location.');
		}
	} catch (error) {
		yield put(devicesActions.deleteDeviceLocationError(error));
	}
}

//🔥 Actions here!, After deleting device location
export function* deleteDeviceLocationSuccess() {
	yield put(devicesActions.loadAllDevicesLocations());
}

//✅ Device Sites
export function* updateDeviceSites({ payload }) {
	try {
		const { deviceID, siteIDs } = payload;
		const result = yield call(() => SuperFetch.put(`/devices/${deviceID}/sites`, { siteIDs }));
		if (result.statusCode === 200) {
			Notification('success', 'Device sites updated successfully.');
			yield put(devicesActions.loadAllDevicesLocations());
		} else {
			throw Error(result.error?.message || 'Failed to update device sites.');
		}
	} catch (error) {
		Notification('error', error.message || 'Failed to update device sites.');
		yield put(devicesActions.updateDeviceSitesError(error));
	}
}

//✅ Devices Helpers
export function* dispatchDevicesAction({ payload }) {
	const { deviceIDs, categoryIDs, actionID, platform, method } = payload;

	try {
		const data = { deviceIDs, categoryIDs, actionID, platform, method };
		const action = yield call(() => SuperFetch.post('/devices/action', data));
		if (action.statusCode === 200) {
			yield put(devicesActions.actionDevicesSuccess());
			Notification('success', 'Unlock Lock(s) Successfully.');
		} else {
			Notification('error', 'Failed to Unlock Lock(s).');
			throw Error(action.error.message || 'Failed to dispatch devices action.');
		}
	} catch (error) {
		yield put(devicesActions.actionDevicesError(error));
	}
}

export default function* devicesSaga() {
	//✅ Devices
	yield takeLatest(devicesActions.loadDevices, getDevices);
	yield takeLatest(devicesActions.loadDevicesSuccess, getDevicesSuccess);
	yield takeLatest(devicesActions.loadAllDevicesLocations, getAllDevicesLocations);
	yield takeLatest(devicesActions.createDevice, createDevice);
	yield takeLatest(devicesActions.createDeviceSuccess, createDeviceSuccess);
	yield takeLatest(devicesActions.createMultipleDevices, createMultipleDevices);
	yield takeLatest(devicesActions.createMultipleDevicesSuccess, createMultipleDevicesSuccess);
	yield takeLatest(devicesActions.updateDevice, updateDevice);
	yield takeLatest(devicesActions.updateDeviceSuccess, updateDeviceSuccess);
	yield takeLatest(devicesActions.deleteDevice, deleteDevice);
	yield takeLatest(devicesActions.deleteDeviceSuccess, deleteDeviceSuccess);

	//✅  Devices Fields
	yield takeLatest(devicesActions.loadDeviceFields, getDeviceFields);
	yield takeLatest(devicesActions.createDeviceField, createDeviceField);
	yield takeLatest(devicesActions.createDeviceFieldSuccess, createDeviceFieldSuccess);
	yield takeLatest(devicesActions.updateDeviceField, updateDeviceField);
	yield takeLatest(devicesActions.updateDeviceFieldSuccess, updateDeviceFieldSuccess);
	yield takeLatest(devicesActions.deleteDeviceField, deleteDeviceField);
	yield takeLatest(devicesActions.deleteDeviceFieldSuccess, deleteDeviceFieldSuccess);

	//✅  Devices Location
	yield takeLatest(devicesActions.loadDeviceLocation, getDeviceLocation);
	yield takeLatest(devicesActions.createDeviceLocation, createDeviceLocation);
	yield takeLatest(devicesActions.createDeviceLocationSuccess, createDeviceLocationSuccess);
	yield takeLatest(devicesActions.updateDeviceLocation, updateDeviceLocation);
	yield takeLatest(devicesActions.updateDeviceLocationSuccess, updateDeviceLocationSuccess);
	yield takeLatest(devicesActions.deleteDeviceLocation, deleteDeviceLocation);
	yield takeLatest(devicesActions.deleteDeviceLocationSuccess, deleteDeviceLocationSuccess);

	//✅  Device Sites
	yield takeLatest(devicesActions.updateDeviceSites, updateDeviceSites);

	//✅  Devices Actions
	yield takeLatest(devicesActions.actionDevices, dispatchDevicesAction);
}
